/**
 * Collect previous sibling elements that should become the base layer
 * for the current parallax section.
 *
 * @param {HTMLElement} overlay Parallax group element.
 * @return {HTMLElement[]} Base layer elements.
 */
const collectParallaxBaseElements = ( overlay ) => {

	const elements = [];
	let previousElement = overlay.previousElementSibling;

	while ( previousElement ) {

		if (
			previousElement.matches( '[data-parallax-base="true"]' ) ||
			previousElement.matches( '[data-parallax-stack="true"]' ) ||
			previousElement.matches( '.wp-block-group[data-parallax="true"]' )
		) {

			break;

		}

		elements.unshift( previousElement );
		previousElement = previousElement.previousElementSibling;

	}

	return elements;

};

/**
 * Wrap the previous content into a dedicated base layer container.
 *
 * @param {HTMLElement} overlay Parallax group element.
 * @return {?HTMLElement} Base layer element.
 */
const createParallaxBase = ( overlay ) => {

	if ( overlay.previousElementSibling?.matches( '[data-parallax-base="true"]' ) ) {

		return overlay.previousElementSibling;

	}

	const baseElements = collectParallaxBaseElements( overlay );
	if ( ! baseElements.length || ! overlay.parentElement ) {

		return null;

	}

	const base = document.createElement( 'div' );
	base.className = 'wp-parallax-base alignfull has-global-padding is-layout-constrained';
	base.setAttribute( 'data-parallax-base', 'true' );

	baseElements[ 0 ].before( base );

	baseElements.forEach( ( element ) => {

		base.appendChild( element );

	} );

	return base;

};

/**
 * Create a shared stack wrapper for the base layer and its parallax section.
 *
 * @param {HTMLElement} base Base layer wrapper.
 * @param {HTMLElement} overlay Parallax group element.
 * @return {?HTMLElement} Stack wrapper.
 */
const createParallaxStack = ( base, overlay ) => {

	if ( base.parentElement?.matches( '[data-parallax-stack="true"]' ) ) {

		return base.parentElement;

	}

	if ( ! base.parentElement || base.parentElement !== overlay.parentElement ) {

		return null;

	}

	const stack = document.createElement( 'div' );
	stack.className = 'wp-parallax-stack alignfull has-global-padding is-layout-constrained';
	stack.setAttribute( 'data-parallax-stack', 'true' );

	base.before( stack );
	stack.appendChild( base );
	stack.appendChild( overlay );

	return stack;

};

/**
 * Get direct sticky stack items from a group block.
 *
 * @param {HTMLElement} stickyStack Sticky stack group element.
 * @return {HTMLElement[]} Sticky stack items.
 */
const getStickyStackItems = ( stickyStack ) => {

	const innerContainer = stickyStack.firstElementChild?.matches( '.wp-block-group__inner-container' )
		? stickyStack.firstElementChild
		: null;
	const items = innerContainer ? innerContainer.children : stickyStack.children;

	return Array.from( items ).filter( ( item ) => item instanceof HTMLElement );

};

/**
 * Sticky stack last-item margin experiment.
 * Comment out the call in syncParallaxMetrics() to disable it on the frontend.
 *
 * @param {HTMLElement} stickyStack Sticky stack group element.
 * @return {number} Extra margin for the last sticky stack item.
 */
const getStickyStackLastItemMargin = ( stickyStack ) => {

	const stickyStackItems = getStickyStackItems( stickyStack );
	const lastStickyStackItem = stickyStackItems[ stickyStackItems.length - 1 ];
	const previousStickyStackItem = stickyStackItems[ stickyStackItems.length - 2 ];

	if ( ! lastStickyStackItem || ! previousStickyStackItem ) {

		return 0;

	}

	return Math.max(
		0,
		previousStickyStackItem.offsetHeight - lastStickyStackItem.offsetHeight
	);

};

/**
 * Apply the experimental last-item margin for sticky stacks.
 * Comment out the call in syncParallaxMetrics() to disable it on the frontend.
 *
 * @param {?HTMLElement} stickyStack Sticky stack group element.
 * @return {void}
 */
const applyStickyStackLastItemMargin = ( stickyStack ) => {

	if ( ! stickyStack ) {

		return;

	}

	const stickyStackItems = getStickyStackItems( stickyStack );
	const lastStickyStackItem = stickyStackItems[ stickyStackItems.length - 1 ];

	stickyStackItems.forEach( ( item ) => {

		item.style.removeProperty( 'margin-bottom' );

	} );

	if ( ! lastStickyStackItem ) {

		return;

	}

	lastStickyStackItem.style.marginBottom = `${ getStickyStackLastItemMargin( stickyStack ) }px`;

};

/**
 * Find the last sticky stack section included in the parallax base.
 *
 * @param {HTMLElement[]} baseElements Base layer elements.
 * @return {?HTMLElement} Sticky stack element.
 */
const getLastStickyStackSection = ( baseElements ) => {

	const stickyStackSections = baseElements.filter(
		( element ) => element.matches( '.wp-block-group[data-sticky-stack="true"]' )
	);

	return stickyStackSections.length ? stickyStackSections[ stickyStackSections.length - 1 ] : null;

};

/**
 * Measure how much extra bottom space a sticky stack needs so its last item
 * can reach the sticky top at the moment the following parallax base starts sticking.
 *
 * @param {HTMLElement} base Parallax base wrapper.
 * @param {?HTMLElement} stickyStack Sticky stack group element.
 * @return {{stickyStack: ?HTMLElement, extraBottom: number}} Sticky stack metrics.
 */
const updateStickyStackMetrics = ( base, stickyStack ) => {

	if ( ! stickyStack ) {

		return {
			stickyStack: null,
			extraBottom: 0,
		};

	}

	const stickyStackItems = getStickyStackItems( stickyStack );
	const lastStickyStackItem = stickyStackItems[ stickyStackItems.length - 1 ];

	if ( ! lastStickyStackItem ) {

		return {
			stickyStack,
			extraBottom: 0,
		};

	}

	const stickyItemStyles = window.getComputedStyle( lastStickyStackItem );
	const stickyTop = parseFloat( stickyItemStyles.top );

	if ( stickyItemStyles.position !== 'sticky' || Number.isNaN( stickyTop ) ) {

		return {
			stickyStack,
			extraBottom: 0,
		};

	}

	const currentExtraBottom = parseFloat(
		stickyStack.style.getPropertyValue( '--group-sticky-stack-extra-bottom' )
	) || 0;
	const baseRect = base.getBoundingClientRect();
	const lastStickyStackItemRect = lastStickyStackItem.getBoundingClientRect();
	const baseBottomWithoutExtraBottom = baseRect.bottom - currentExtraBottom;
	const lastStickyStackItemTopAtParallaxStart = lastStickyStackItemRect.top - (
		baseBottomWithoutExtraBottom - window.innerHeight
	);

	return {
		stickyStack,
		extraBottom: Math.max( 0, lastStickyStackItemTopAtParallaxStart - stickyTop ),
	};

};

/**
 * Set sticky top so the base layer stops with its bottom edge
 * aligned to the bottom edge of the viewport.
 *
 * @param {HTMLElement} base Base layer wrapper.
 * @return {{base: HTMLElement, stickyTop: number}} Base metrics payload.
 */
const updateParallaxBaseMetrics = ( base ) => {

	const stickyTop = window.innerHeight - base.offsetHeight;

	return {
		base,
		stickyTop,
	};

};

/**
 * Measure all parallax base offsets first, then write CSS variables in a batch.
 *
 * @param {Array<{base: HTMLElement, stickyStackSection: ?HTMLElement}>} parallaxItems Initialized parallax items.
 */
const syncParallaxMetrics = ( parallaxItems ) => {

	const measurements = parallaxItems.map( ( { base, stickyStackSection } ) => ( {
		baseMetrics: updateParallaxBaseMetrics( base ),
		stickyStackMetrics: updateStickyStackMetrics( base, stickyStackSection ),
	} ) );

	measurements.forEach( ( { baseMetrics, stickyStackMetrics } ) => {

		baseMetrics.base.style.setProperty( '--parallax-base-sticky-top', `${ baseMetrics.stickyTop }px` );

		if ( stickyStackMetrics.stickyStack ) {

			stickyStackMetrics.stickyStack.style.setProperty(
				'--group-sticky-stack-extra-bottom',
				`${ stickyStackMetrics.extraBottom }px`
			);
			applyStickyStackLastItemMargin( stickyStackMetrics.stickyStack );

		}

	} );

};

/**
 * Apply the experimental last-item margin for standalone sticky stacks.
 * Comment out the call in initGroupParallax() to disable it on the frontend.
 *
 * @param {HTMLElement[]} stickyStacks Sticky stack group elements.
 * @return {void}
 */
const syncStandaloneStickyStackMargins = ( stickyStacks ) => {

	stickyStacks.forEach( ( stickyStack ) => {

		applyStickyStackLastItemMargin( stickyStack );

	} );

};

/**
 * Schedule a batched parallax metrics update for the next animation frame.
 *
 * @param {Array<{base: HTMLElement, stickyStackSection: ?HTMLElement}>} parallaxItems Initialized parallax items.
 * @return {Function} Deferred sync callback.
 */
const createParallaxSyncScheduler = ( parallaxItems ) => {

	let animationFrameId = null;

	return () => {

		if ( animationFrameId !== null ) {

			return;

		}

		animationFrameId = window.requestAnimationFrame( () => {

			animationFrameId = null;
			syncParallaxMetrics( parallaxItems );

		} );

	};

};

/**
 * Schedule a batched standalone sticky-stack margin update for the next animation frame.
 *
 * @param {HTMLElement[]} stickyStacks Sticky stack group elements.
 * @return {Function} Deferred sync callback.
 */
const createStickyStackMarginSyncScheduler = ( stickyStacks ) => {

	let animationFrameId = null;

	return () => {

		if ( animationFrameId !== null ) {

			return;

		}

		animationFrameId = window.requestAnimationFrame( () => {

			animationFrameId = null;
			syncStandaloneStickyStackMargins( stickyStacks );

		} );

	};

};

/**
 * Initialize the parallax wrappers for eligible group blocks.
 *
 * @return {void}
 */
const initGroupParallax = () => {

	const parallaxItems = [];

	document.querySelectorAll( '.wp-block-group[data-parallax="true"]' ).forEach( ( overlay ) => {

		const base = createParallaxBase( overlay );
		if ( ! base ) {

			return;

		}

		const stack = createParallaxStack( base, overlay );
		if ( ! stack ) {

			return;

		}

		base.classList.add( 'has-parallax-base' );
		stack.classList.add( 'has-parallax-stack' );
		overlay.classList.add( 'has-parallax-base-before' );

		const stickyStackSection = getLastStickyStackSection(
			Array.from( base.children ).filter( ( element ) => element instanceof HTMLElement )
		);

		parallaxItems.push( { base, overlay, stickyStackSection } );

	} );

	const stickyStacks = Array.from(
		document.querySelectorAll( '.wp-block-group[data-sticky-stack="true"]' )
	).filter(
		( stickyStack ) => ! stickyStack.closest( '[data-parallax-base="true"]' )
	);

	if ( ! parallaxItems.length && ! stickyStacks.length ) {

		return;

	}

	if ( parallaxItems.length ) {

		const scheduleParallaxSync = createParallaxSyncScheduler( parallaxItems );
		scheduleParallaxSync();
		window.addEventListener( 'resize', scheduleParallaxSync );

		if ( 'ResizeObserver' in window ) {

			const resizeObserver = new ResizeObserver( scheduleParallaxSync );

			parallaxItems.forEach( ( { base, overlay, stickyStackSection } ) => {

				resizeObserver.observe( base );
				resizeObserver.observe( overlay );

				if ( stickyStackSection ) {

					resizeObserver.observe( stickyStackSection );

				}

			} );

		}

	}

	if ( stickyStacks.length ) {

		const scheduleStickyStackMarginSync = createStickyStackMarginSyncScheduler( stickyStacks );

		// Comment out this call to disable standalone sticky-stack last-item margins on the frontend.
		scheduleStickyStackMarginSync();
		window.addEventListener( 'resize', scheduleStickyStackMarginSync );

		if ( 'ResizeObserver' in window ) {

			const resizeObserver = new ResizeObserver( scheduleStickyStackMarginSync );

			stickyStacks.forEach( ( stickyStack ) => {

				getStickyStackItems( stickyStack ).forEach( ( item ) => {

					resizeObserver.observe( item );

				} );

			} );

		}

	}

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', initGroupParallax );

} else {

	initGroupParallax();

}
